import React from 'react';

import AddIcon from '@mui/icons-material/Add';

import {
  Typography,
} from '@mui/material';

import {
  GridColDef,
  GridValidRowModel,
} from '@mui/x-data-grid';

import {
  FDataGrid,
  FGridCodeCombo,
} from '../grid';

import {makeTempRowId} from '../grid/FGridUtils';

type Props={
  rows:any[];
  loading?:boolean;
  selectedTask:any|null;
  accounts?:any[];
  onChange:(rows:any[])=>void;
  onSubmit:(rows:any[])=>Promise<void>;
};

export default class BudgetLineGridPanel
extends React.PureComponent<Props>{

  private getRowId=(row:any)=>
    row.id
    ??row.rid
    ??row.budgetlineid;

  private add=()=>{
    const taskplanid=Number(
      this.props.selectedTask?.taskplanid
      ??this.props.selectedTask?.rid
      ??0,
    );

    if(!taskplanid) return;

    const id=makeTempRowId('budgetline');

    this.props.onChange([
      ...(this.props.rows||[]),
      {
        id,
        rid:null,
        budgetlineid:null,
        taskplanid,

        accountid:null,
        accountcode:'',
        accountname:'',

        description:'',
        quantity:1,
        unitprice:0,
        amount:0,
        remarks:'',

        status:1,
        _state:'new',
        __dirty:true,
        __error:'Account is required.',
      },
    ]);
  };

  private validate=(row:any)=>{
    if(!String(row.accountcode||'').trim())
      return 'Account is required.';

    if(Number(row.quantity||0)<0)
      return 'Quantity cannot be negative.';

    if(Number(row.unitprice||0)<0)
      return 'Unit price cannot be negative.';

    return '';
  };

  private processRowUpdate=(
    newRow:GridValidRowModel,
    oldRow:GridValidRowModel,
  )=>{
    const updated={
      ...oldRow,
      ...newRow,

      quantity:Number(newRow.quantity??0),
      unitprice:Number(newRow.unitprice??0),

      amount:
        Number(newRow.quantity??0)
        *Number(newRow.unitprice??0),

      _state:
        oldRow.rid||oldRow.budgetlineid
          ?'dirty'
          :'new',

      __dirty:true,
      __error:'',
    };

    updated.__error=this.validate(updated);

    this.props.onChange(
      this.props.rows.map(
        row=>
          String(this.getRowId(row))
          ===String(this.getRowId(updated))
            ?updated
            :row,
      ),
    );

    return updated;
  };

  private renderAccountEditor=(params:any)=>(
    <FGridCodeCombo
      {...params}
      label="Account"
      items={this.props.accounts||[]}
      value={params.value||''}
      type="CODE"
      autoOpen
    />
  );

  private columns=():GridColDef[]=>[
    {
      field:'accountcode',
      headerName:'Account',
      width:170,
      editable:true,
      renderEditCell:this.renderAccountEditor,
      cellClassName:'budget-cell-input',
    },
    {
      field:'accountname',
      headerName:'Account Name',
      minWidth:260,
      flex:1,
      editable:false,
      cellClassName:'budget-cell-master',
    },
    {
      field:'description',
      headerName:'Description',
      minWidth:260,
      flex:1,
      editable:true,
      cellClassName:'budget-cell-input',
    },
    {
      field:'quantity',
      headerName:'Quantity',
      width:120,
      type:'number',
      editable:true,
      cellClassName:'budget-cell-input',
    },
    {
      field:'unitprice',
      headerName:'Unit Price',
      width:140,
      type:'number',
      editable:true,
      cellClassName:'budget-cell-input',
    },
    {
      field:'amount',
      headerName:'Amount',
      width:160,
      type:'number',
      editable:false,
      cellClassName:'budget-cell-computed',
    },
    {
      field:'remarks',
      headerName:'Remarks',
      minWidth:220,
      flex:1,
      editable:true,
      cellClassName:'budget-cell-input',
    },
    {
      field:'__error',
      headerName:'Validation',
      minWidth:220,
      editable:false,
      cellClassName:'budget-cell-validation',
      renderCell:params=>
        params.value
          ?(
            <Typography variant="caption" color="error.main">
              {params.value}
            </Typography>
          )
          :null,
    },
  ];

  private save=async()=>{
    await this.props.onSubmit(this.props.rows);
  };

  render(){
    const dirtyCount=this.props.rows.filter(
      row=>
        row.__dirty
        ||row._state==='new'
        ||row._state==='dirty'
        ||row._state==='deleted',
    ).length;

    const invalidCount=this.props.rows.filter(
      row=>Boolean(row.__error||this.validate(row)),
    ).length;

    const taskName=
      this.props.selectedTask?.name
      ??this.props.selectedTask?.taskname
      ??'';

    return (
      <FDataGrid
        title="Budget Lines"
        subtitle={`Task: ${taskName}. Amber cells are planner inputs; blue cells are computed.`}
        rows={this.props.rows||[]}
        columns={this.columns()}
        loading={this.props.loading}
        height={370}
        getRowId={this.getRowId}
        processRowUpdate={this.processRowUpdate}
        onProcessRowUpdateError={console.error}
        isCellEditable={params=>
          [
            'accountcode',
            'description',
            'quantity',
            'unitprice',
            'remarks',
          ].includes(params.field)
        }
        toolbarActions={[
          {
            key:'add-budget-line',
            label:'Add Budget Line',
            icon:<AddIcon fontSize="small"/>,
            onClick:this.add,
          },
        ]}
        saveAll={
          dirtyCount===0||invalidCount>0
            ?undefined
            :this.save
        }
        getRowClassName={params=>
          params.row.__error
            ?'budget-row-error'
            :params.row.__dirty
              ?'budget-row-dirty'
              :'budget-row-clean'
        }
        sx={{
          '& .budget-cell-input':{
            bgcolor:'warning.lighter',
            fontWeight:600,
          },
          '& .budget-cell-computed':{
            bgcolor:'info.lighter',
            fontWeight:600,
          },
          '& .budget-cell-master':{
            bgcolor:'action.hover',
          },
          '& .budget-cell-validation':{
            color:'error.main',
          },
          '& .budget-row-dirty':{
            boxShadow:'inset 3px 0 0 0',
            boxShadowColor:'warning.main',
          },
          '& .budget-row-error':{
            bgcolor:'error.lighter',
          },
        }}
      />
    );
  }
}
